import {Localizer} from "@/main/react/Types";
import {getFirstFailedValidationMessage, type ValidationRule} from "@/main/validation/requirements";
import {PUBLIC_ID_PATTERN, REQUEST_VALIDATION_LIMITS} from "@/main/validation/request";

export type GroupItemValidationKind = "rule" | "resource" | "related";

export type GroupItemValidationValues = {
    title: string;
    description: string;
    url: string;
    relatedGroupPublicId: string;
};

function createTitleValidationRules(localizer: Localizer): ValidationRule[] {
    return [
        {
            id: "length",
            text: localizer("group.item.validation.title.size", REQUEST_VALIDATION_LIMITS.groupOrSeriesName.maxLength),
            validate: (value: string) => value.trim().length > 0
                && value.trim().length <= REQUEST_VALIDATION_LIMITS.groupOrSeriesName.maxLength,
        },
    ];
}

function createDescriptionValidationRules(localizer: Localizer): ValidationRule[] {
    return [
        {
            id: "max",
            text: localizer("group.item.validation.description.size", REQUEST_VALIDATION_LIMITS.longText.maxLength),
            validate: (value: string) => value.trim().length <= REQUEST_VALIDATION_LIMITS.longText.maxLength,
        },
    ];
}

function createUrlValidationRules(localizer: Localizer): ValidationRule[] {
    return [
        {
            id: "length",
            text: localizer("group.item.validation.url.size", REQUEST_VALIDATION_LIMITS.url.maxLength),
            validate: (value: string) => value.trim().length > 0 && value.trim().length <= REQUEST_VALIDATION_LIMITS.url.maxLength,
        },
    ];
}

function createRelatedGroupValidationRules(localizer: Localizer): ValidationRule[] {
    return [
        {
            id: "public-id",
            text: localizer("group.item.validation.related_group_public_id.pattern", REQUEST_VALIDATION_LIMITS.publicIdLength),
            validate: (value: string) => PUBLIC_ID_PATTERN.test(value.trim()),
        },
    ];
}

export function validateGroupItemValues(kind: GroupItemValidationKind, values: GroupItemValidationValues, localizer: Localizer) {
    if (kind === "related") {
        return getFirstFailedValidationMessage(createRelatedGroupValidationRules(localizer), values.relatedGroupPublicId);
    }

    return getFirstFailedValidationMessage(createTitleValidationRules(localizer), values.title)
        ?? getFirstFailedValidationMessage(createDescriptionValidationRules(localizer), values.description)
        ?? (kind === "resource"
            ? getFirstFailedValidationMessage(createUrlValidationRules(localizer), values.url)
            : null);
}
